import { get, post } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';

export interface AnalyticsMetrics {
  activeUsers?: string;
  sessions?: string;
  screenPageViews?: string;
  engagementRate?: string;
  averageSessionDuration?: string;
  newUsers?: string;
  bounceRate?: string;
  eventsPerSession?: string;
}

export interface AnalyticsData {
  data: {
    metrics: AnalyticsMetrics;
    dimensions: any[];
    rows: any[];
  };
  dateRange: {
    startDate: string;
    endDate: string;
  };
  propertyId: string;
  timestamp: string;
}

export interface AnalyticsRequest {
  metrics?: string[];
  startDate?: string;
  endDate?: string;
  dimensions?: string[];
}

class AnalyticsService {
  private static instance: AnalyticsService;
  private cache: Map<string, { data: AnalyticsData; timestamp: number }> = new Map();
  private cacheTimeout = 5 * 60 * 1000; // 5 minutes cache

  private constructor() {
    // Private constructor for singleton pattern
  }

  public static getInstance(): AnalyticsService {
    if (!AnalyticsService.instance) {
      AnalyticsService.instance = new AnalyticsService();
    }
    return AnalyticsService.instance;
  }

  private getCacheKey(request: AnalyticsRequest): string {
    return JSON.stringify(request);
  }

  private isCacheValid(timestamp: number): boolean {
    return Date.now() - timestamp < this.cacheTimeout;
  }

  public async fetchAnalytics(request: AnalyticsRequest = {}): Promise<AnalyticsData> {
    const cacheKey = this.getCacheKey(request);
    const cached = this.cache.get(cacheKey);

    if (cached && this.isCacheValid(cached.timestamp)) {
      console.log('Returning cached analytics data');
      return cached.data;
    }

    try {
      console.log('Fetching fresh analytics data');

      // Get the auth session to include the token
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      if (!token) {
        throw new Error('No authentication token available');
      }

      const queryParams = new URLSearchParams({
        metrics: request.metrics?.join(',') || 'activeUsers,sessions,screenPageViews,engagementRate,averageSessionDuration,newUsers',
        startDate: request.startDate || '7daysAgo',
        endDate: request.endDate || 'today',
        ...(request.dimensions && { dimensions: request.dimensions.join(',') })
      });

      const restOperation = get({
        apiName: 'adminApi',
        path: `/analytics?${queryParams.toString()}`,
        options: {
          headers: {
            Authorization: token
          }
        }
      });

      const { body } = await restOperation.response;
      const jsonData = await body.json();
      const response = jsonData as unknown as AnalyticsData;

      // Cache the response
      this.cache.set(cacheKey, {
        data: response,
        timestamp: Date.now()
      });

      return response;
    } catch (error) {
      console.error('Error fetching analytics:', error);
      throw error;
    }
  }

  public async fetchDashboardMetrics(): Promise<AnalyticsData> {
    return this.fetchAnalytics({
      metrics: [
        'activeUsers',
        'sessions',
        'screenPageViews',
        'engagementRate',
        'averageSessionDuration',
        'newUsers'
      ],
      startDate: '7daysAgo',
      endDate: 'today'
    });
  }

  public async fetchUserTrends(): Promise<AnalyticsData> {
    return this.fetchAnalytics({
      metrics: ['activeUsers', 'newUsers', 'sessions'],
      dimensions: ['date'],
      startDate: '30daysAgo',
      endDate: 'today'
    });
  }

  public async fetchPagePerformance(): Promise<AnalyticsData> {
    return this.fetchAnalytics({
      metrics: ['screenPageViews', 'averageSessionDuration', 'bounceRate'],
      dimensions: ['pagePath'],
      startDate: '7daysAgo',
      endDate: 'today'
    });
  }

  public async fetchDeviceBreakdown(): Promise<AnalyticsData> {
    return this.fetchAnalytics({
      metrics: ['activeUsers', 'sessions'],
      dimensions: ['deviceCategory'],
      startDate: '7daysAgo',
      endDate: 'today'
    });
  }

  public async fetchTrafficSources(): Promise<AnalyticsData> {
    return this.fetchAnalytics({
      metrics: ['activeUsers', 'newUsers', 'sessions'],
      dimensions: ['sessionSource'],
      startDate: '7daysAgo',
      endDate: 'today'
    });
  }

  public async fetchRealtimeUsers(): Promise<AnalyticsData> {
    return this.fetchAnalytics({
      metrics: ['activeUsers'],
      startDate: 'today',
      endDate: 'today'
    });
  }

  public clearCache(): void {
    this.cache.clear();
  }
}

export default AnalyticsService.getInstance();